/**
 * Componente principal do mapa
 */

import React, { useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Circle, Marker } from '@react-google-maps/api';
import { Box, CircularProgress, Alert } from '@mui/material';
import { config, APP_CONSTANTS } from '../../utils/config';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { setCenter, setSelectedLocation } from '../../store/slices/mapSlice';
import { Coordinates } from '../../types';
import { googlePlacesService } from '../../services/googlePlacesService';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const libraries: ('places' | 'geometry')[] = ['places', 'geometry'];

const MapComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { center, zoom, selectedLocation, radius, isSelectingLocation } = useAppSelector(
    (state) => state.map
  );
  const { results } = useAppSelector((state) => state.search);
  
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleMapsApiKey,
    libraries
  });

  const onLoad = useCallback((map: google.maps.Map) => {
    // Inicializar Places Service
    googlePlacesService.initialize(map);
  }, []);

  const onUnmount = useCallback(() => {
    // Cleanup
  }, []);

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (isSelectingLocation && e.latLng) {
        const coordinates: Coordinates = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        };
        dispatch(setSelectedLocation(coordinates));
        dispatch(setCenter(coordinates));
      }
    },
    [isSelectingLocation, dispatch]
  );

  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Erro ao carregar Google Maps. Verifique sua API Key.
        </Alert>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={handleMapClick}
      options={{
        ...APP_CONSTANTS.MAP_OPTIONS,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      }}
    >
      {/* Círculo de área de busca */}
      {selectedLocation && (
        <Circle
          center={selectedLocation}
          radius={radius}
          options={APP_CONSTANTS.CIRCLE_OPTIONS}
        />
      )}

      {/* Marcador do ponto selecionado */}
      {selectedLocation && (
        <Marker
          position={selectedLocation}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#FF5722',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3
          }}
        />
      )}

      {/* Marcadores dos resultados */}
      {results.map((business) => (
        <Marker
          key={business.id}
          position={business.coordinates}
          title={business.name}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#4CAF50',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2
          }}
        />
      ))}
    </GoogleMap>
  );
};

export default MapComponent;

